import { useState } from 'react';
import { AlertCircle, ArrowRight, Check, ChevronDown, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { ConfigItemStatusBadge } from './ConfigItemStatusBadge';
import { allowedMoves, labelFor, normalizeStatus, type LifecycleStatus, type Move } from '../utils/configItemLifecycle';
import { readyForBuildErrors } from '../utils/configItemReadiness';

/**
 * ConfigItemStatusControl: the moves open to the viewer on a config item's one lifecycle
 * status (docs/CONFIG_ITEM_LIFECYCLE.md). The client locks, approves and sends back; the firm
 * builds, hands to review, resets, approves on the client's behalf (note required) and blocks.
 * The server applies the same table, so a move offered here is a move it accepts.
 *
 * Locking for build (discovery → ready for build) waits until the item is ready: with questions
 * unanswered, AI suggestions unconfirmed or follow-ups open, the move shows what is missing
 * instead (RESTORE_PLAN R2.8, the old ImplementationStatusControl's gate).
 */

interface ConfigItemStatusControlProps {
  item: Record<string, any>;
  /** A member of the consulting firm (or a system admin). */
  firm: boolean;
  onMove: (to: LifecycleStatus, comment?: string) => void | Promise<void>;
  className?: string;
  /**
   * What stops a lock for build; when absent it is read from the item's questions
   * (utils/configItemReadiness). Pass it when `item` is only a summary (the Queue's rows).
   */
  validationErrors?: string[];
}

export interface MoveAction extends Move {
  label: string;
  description: string;
}

/** What each move is called on screen, from where it starts and who makes it. */
export function moveActions(from: LifecycleStatus, firm: boolean, blockedFrom?: unknown): MoveAction[] {
  return allowedMoves(from, { firm, blockedFrom }).map((move) => {
    // Unblock returns to where the item stopped, which may be any status; name it as an unblock.
    if (from === 'blocked') {
      return { ...move, label: `Unblock (back to ${labelFor(move.to).toLowerCase()})`, description: 'Work can continue where it stopped.' };
    }
    switch (move.to) {
      case 'ready_for_build':
        return from === 'discovery'
          ? { ...move, label: 'Lock for build', description: 'The configuration is settled. Nothing changes until the firm builds it or a consultant unlocks it with a reason.' }
          : { ...move, label: 'Reset to ready for build', description: 'Stop the build and put the item back in the queue.' };
      case 'in_build':
        return from === 'ready_for_review'
          ? { ...move, label: 'Send back', description: 'The build is not right yet; say what to change.' }
          : { ...move, label: 'Start build', description: 'The firm is building this item now.' };
      case 'ready_for_review':
        return { ...move, label: 'Hand to review', description: 'Built; ask the client to confirm it.' };
      case 'approved':
        return firm
          ? { ...move, label: "Approve on the client's behalf", description: 'The client confirmed the build outside the app; note where.' }
          : { ...move, label: 'Approve', description: 'Confirm the build is correct and complete.' };
      case 'blocked':
        return { ...move, label: 'Block', description: 'Stop work on this item; it returns to where it was when unblocked.' };
      default:
        return { ...move, label: `Unblock (back to ${labelFor(move.to).toLowerCase()})`, description: 'Work can continue where it stopped.' };
    }
  });
}

export function ConfigItemStatusControl({ item, firm, onMove, className = '', validationErrors }: ConfigItemStatusControlProps) {
  const [pending, setPending] = useState<MoveAction | null>(null);
  const [blockedBy, setBlockedBy] = useState<string[] | null>(null);
  const [comment, setComment] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const status = normalizeStatus(item);
  const actions = moveActions(status, firm, item.blockedFrom);
  const history: any[] = Array.isArray(item.statusHistory) ? item.statusHistory : [];

  // Locking for build waits until the item is ready (the old "Cannot mark as ready for build").
  const choose = (action: MoveAction) => {
    if (status === 'discovery' && action.to === 'ready_for_build') {
      const errors = validationErrors ?? readyForBuildErrors(item);
      if (errors.length > 0) {
        setBlockedBy(errors);
        return;
      }
    }
    setPending(action);
    setComment('');
  };

  const confirm = () => {
    if (!pending) return;
    const text = comment.trim();
    if (pending.requiresComment && !text) return;
    void onMove(pending.to, text || undefined);
    setPending(null);
    setComment('');
  };

  return (
    <div className={`space-y-2 ${className}`} data-testid="config-item-status-control">
      <div className="flex flex-wrap items-center gap-2">
        <ConfigItemStatusBadge item={item} />

        {actions.length === 1 && (
          <Button size="sm" variant={actions[0].to === 'approved' ? 'default' : 'outline'} onClick={() => choose(actions[0])}>
            <ArrowRight className="mr-2 h-4 w-4" aria-hidden="true" />
            {actions[0].label}
          </Button>
        )}
        {actions.length > 1 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline">
                Change status
                <ChevronDown className="ml-2 h-4 w-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel>Available moves</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {actions.map((action) => (
                <DropdownMenuItem key={action.to} onClick={() => choose(action)}>
                  <div>
                    <div className="flex items-center gap-2">
                      <span>{action.label}</span>
                      {action.requiresComment && <Badge variant="secondary" className="text-xs">Comment required</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{action.description}</p>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {history.length > 0 && (
          <Button variant="ghost" size="sm" onClick={() => setShowHistory((v) => !v)}>
            <MessageSquare className="h-4 w-4 mr-2" aria-hidden="true" />
            {history.length} {history.length === 1 ? 'change' : 'changes'}
          </Button>
        )}
      </div>

      {showHistory && history.length > 0 && (
        <Card className="mt-2">
          <CardHeader>
            <CardTitle className="text-sm">Status history</CardTitle>
            <CardDescription>Every move and who made it</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {[...history].reverse().map((entry, i) => (
              <div key={entry.timestamp ? `${entry.timestamp}-${i}` : i} className="border-l-2 border-primary/20 pl-3 py-2">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="text-xs">{labelFor(entry.fromStatus)} → {labelFor(entry.toStatus)}</Badge>
                  {entry.timestamp && <span className="text-xs text-muted-foreground">{new Date(entry.timestamp).toLocaleString()}</span>}
                </div>
                {entry.comment && <p className="text-sm">{entry.comment}</p>}
                <p className="text-xs text-muted-foreground mt-1">by {entry.changedBy?.userName || entry.changedBy?.userEmail || 'unknown'}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <AlertDialog open={!!pending} onOpenChange={(open) => { if (!open) { setPending(null); setComment(''); } }}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{pending?.label}</AlertDialogTitle>
            <AlertDialogDescription>{pending?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 bg-muted rounded-md text-sm">
              <span className="text-muted-foreground">Now: </span>
              <strong>{labelFor(status)}</strong>
              <ArrowRight className="inline h-4 w-4 mx-2" aria-hidden="true" />
              <span className="text-muted-foreground">Next: </span>
              <strong>{pending ? labelFor(pending.to) : ''}</strong>
            </div>
            <div className="space-y-2">
              <Label htmlFor="config-item-status-comment">{pending?.requiresComment ? 'Comment (required)' : 'Comment (optional)'}</Label>
              <Textarea
                id="config-item-status-comment"
                placeholder={pending?.requiresComment ? 'Say what needs to change, or where the client confirmed…' : 'Add a note about this move (optional)…'}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                className={pending?.requiresComment && !comment.trim() ? 'border-orange-500' : ''}
              />
            </div>
          </div>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => { setPending(null); setComment(''); }}>Cancel</Button>
            <Button onClick={confirm} disabled={!!pending?.requiresComment && !comment.trim()}>
              <Check className="mr-2 h-4 w-4" aria-hidden="true" />
              Confirm
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ReadyForBuildBlockedDialog errors={blockedBy} itemName={item?.name} onClose={() => setBlockedBy(null)} />
    </div>
  );
}

/** "Cannot lock for build": what is missing, one line each. Shared by every lock button. */
export function ReadyForBuildBlockedDialog({ errors, itemName, onClose }: { errors: string[] | null; itemName?: string; onClose: () => void }) {
  return (
    <AlertDialog open={!!errors && errors.length > 0} onOpenChange={(open) => { if (!open) onClose(); }}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Cannot lock for build yet</AlertDialogTitle>
          <AlertDialogDescription>
            {itemName ? `${itemName} needs` : 'This item needs'} the following before it can be locked for build:
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>
            <ul className="list-disc list-inside space-y-1 mt-1" data-testid="ready-for-build-errors">
              {(errors ?? []).map((error, i) => <li key={i}>{error}</li>)}
            </ul>
          </AlertDescription>
        </Alert>
        <AlertDialogFooter>
          <Button onClick={onClose}>OK</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
